import {
    ChangeDetectionStrategy,
    Component,
    computed,
    HostBinding,
    inject,
    input,
    ViewEncapsulation,
    Signal,
} from '@angular/core';
import { FiRemoteStateFullDataSource } from '@fi-shared/fi-items-list/fi-remote-state-full-data-source';
import { IChecklist2TemplateParameter, IChecklistV2TemplateForList } from '@fi-shared/checklist/checklist-v2';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import { ObjectTypes } from '@fi-shared/types';
import { IItemsListAction2, ISideListOptionsItem } from '@fi-shared/fi-items-list/fi-items-list';
import { FiChecklist2TemplateService } from '@fi-shared/checklist/fi-checklist2-template/fi-checklist2-template.service';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiChecklistActionsHelperService } from '@fi-shared/checklist/fi-checklist-actions-helper.service';
import { FiChecklistHelperService } from '@fi-shared/checklist/fi-checklist-helper.service';
import { IItemsListDataFetchResult } from '@fi-shared/fi-items-list/fi-items-list';
import { FiChecklist2SideListOptionsHelperService } from '@fi-shared/checklist/fi-checklist2-side-list-options-helper.service';

@Component({
    selector: 'fi-checklist2-template-list',
    templateUrl: './fi-checklist2-template-list.component.html',
    styleUrls: ['./fi-checklist2-template-list.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-checklist2-template-list' },
    standalone: false,
})
export class FiChecklist2TemplateListComponent extends FiRemoteStateFullDataSource<IChecklistV2TemplateForList> {
    public auditMode = input.required<boolean>();
    @HostBinding('class.mobile-view') get isMobileView(): boolean {
        return this.detectDeviceService.isMobilePhone();
    }

    private fiChecklist2TemplateService = inject(FiChecklist2TemplateService);
    private detectDeviceService = inject(DetectDevicesService);
    private fiChecklistHelperService = inject(FiChecklistHelperService);
    private fiChecklistActionsHelperService = inject(FiChecklistActionsHelperService);
    private fiChecklist2SideListOptionsHelperService = inject(FiChecklist2SideListOptionsHelperService);

    public defaultSortColumnName: string = 'Name';
    public defaultSortDirection: boolean = false;
    public objectType = ObjectTypes.Checklist2Template;
    public itemsListType = FiItemsListType.ChecklistV2Templates;

    protected sideListOptions: Signal<ISideListOptionsItem[]> = computed(() =>
        this.fiChecklist2SideListOptionsHelperService.getChecklistTemplateSideListOptions(this.auditMode()),
    );
    public actions: Signal<IItemsListAction2<IChecklistV2TemplateForList>[]> = computed(() =>
        this.getChecklistTemplateListActions(),
    );

    private rootState = computed(() => FiStateNames.getChecklistV2RootState(this.auditMode()));
    protected viewByTemplateState = computed(() => this.rootState().ViewByChecklistTemplate);

    protected getRowClassFn = (): string => 'cursor-pointer';

    public loadListDataFn(loadArgs: ILoadDataArgs): Promise<IItemsListDataFetchResult<IChecklistV2TemplateForList>> {
        const parameter = this.getItemsListParameter(loadArgs) as IChecklist2TemplateParameter;
        parameter.FetchParams = {
            FetchItems: true,
            FetchTotalCount: !loadArgs.fetchOnlyItems,
            FetchLightWeightObjects: !loadArgs.fetchOnlyItems,
        };
        parameter.AuditMode = this.auditMode();
        return this.fiChecklist2TemplateService.getChecklistTemplatesListData(parameter);
    }

    protected getViewByTemplateStateParams(item: IChecklistV2TemplateForList): { id: number } {
        return { id: item.Id };
    }

    protected openChecklistTemplateView(item: IChecklistV2TemplateForList): void {
        const viewState = this.viewByTemplateState();
        const params = this.getViewByTemplateStateParams(item);
        this.fiStateService.go(viewState, params);
    }

    private getChecklistTemplateListActions(): IItemsListAction2<IChecklistV2TemplateForList>[] {
        const auditMode = this.auditMode();
        return [
            this.fiChecklistActionsHelperService.getChecklistTemplateEditAction<IChecklistV2TemplateForList>({
                checklistTemplateIdFn: (data) => data.Id,
                auditMode: auditMode,
                templateMode: true,
            }),
            this.fiChecklistActionsHelperService.getCreateChecklistFromTemplateAction<IChecklistV2TemplateForList>({
                checklistTemplateIdFn: (data) => data.Id,
                auditMode: auditMode,
                templateMode: true,
            }),
            this.fiChecklistActionsHelperService.getDeleteChecklistTemplateAction<IChecklistV2TemplateForList>({
                onClick: (data) => {
                    this.fiChecklistHelperService.getConfirmTemplateDeleteDialog().onYesButtonClick(() => {
                        this.fiChecklist2TemplateService.deleteChecklistTemplates([data.Id], auditMode).then(() => {
                            this.fiChecklistHelperService.showTemplateDeletedSnackbar(data, auditMode);
                            this.removeData([data]); // for excluding deleted checklist template from list
                        });
                    });
                },
                auditMode: auditMode,
                templateMode: true,
            }),
        ];
    }
}
